import React, { useEffect, useState, useMemo, useRef } from "react";
import { apiGet, apiPost, apiPut } from "../api/http";
import "../styles/technologies.css";
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ClearIcon from '@mui/icons-material/Clear';
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { dateFormat } from "../utils/dateFormat";
import dayjs from "dayjs";
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';

// Normalize technology name for duplicate check
function normalizeTechName(name: string): string {
  return name
    .replace(/[^a-zA-Z0-9]/g, "") // Remove all non-alphanumeric chars
    .replace(/\s+/g, "") // Remove all spaces
    .toLowerCase();
}

// Utility hook to check if text is truncated
function useIsTruncated<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [truncated, setTruncated] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (el) {
      setTruncated(el.scrollWidth > el.clientWidth);
    }
  });
  return [ref, truncated] as const;
}

// Row component for table to use hooks safely
const TechnologyTableRow: React.FC<{ t: Technology; openEditModal: (t: Technology) => void }> = ({ t, openEditModal }) => {
  const [createdByRef, createdByTruncated] = useIsTruncated<HTMLSpanElement>();
  const [modifiedByRef, modifiedByTruncated] = useIsTruncated<HTMLSpanElement>();
  return (
    <tr key={t.techGroupId}>
      <td>{t.technologyName}</td>
      <td>
        <Tooltip
          title={t.createdBy}
          arrow
          disableHoverListener={!createdByTruncated}
        >
          <span
            ref={createdByRef}
            style={{ display: 'inline-block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom' }}
          >
            {t.createdBy}
          </span>
        </Tooltip>
      </td>
      <td>{dateFormat(t.createdDate)}</td>
      <td>
        <Tooltip
          title={t.modifiedBy || ''}
          arrow
          disableHoverListener={!modifiedByTruncated}
        >
          <span
            ref={modifiedByRef}
            style={{ display: 'inline-block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom' }}
          >
            {t.modifiedBy}
          </span>
        </Tooltip>
      </td>
      <td>{dateFormat(t.modifiedDate)}</td>
      <td>
        <span className={
          t.status === "Active"
            ? "badge badge-active"
            : "badge badge-inactive"
        }>
          {t.status}
        </span>
      </td>
      <td className="action-cell">
        <Tooltip title="Edit Technology" arrow>
          <button className="edit-btn" onClick={() => openEditModal(t)} tabIndex={0}>
            <LocalIcon src="/icons/EditIcon.svg" className="edit-icon" />
          </button>
        </Tooltip>
      </td>
    </tr>
  );
};


type Technology = {
  techGroupId: number;
  technologyName: string;
  createdBy: string;
  createdDate: string;
  modifiedBy: string | null;
  modifiedDate: string | null;
  status: string;
};

type LocalIconProps = {
    src: string;
    alt?: string;
    className?: string;
};

const LocalIcon: React.FC<LocalIconProps> = ({ src, alt = "", className }) => (
    <img
        src={src}
        alt={alt}
        className={className}
        loading="lazy"
    />
);

const PAGE_SIZE = 10;

const TechnologiesDetails: React.FC = () => {
  const [technologies, setTechnologies] = useState<Technology[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortColumn, setSortColumn] = useState<keyof Technology | null>("createdDate");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [name, setName] = useState("");
  // Status filter for table: null = placeholder, 'Active'/'Inactive' = selected
  const [filterStatus, setFilterStatus] = useState<string | null>(null);
  // Separate input value for Autocomplete (for clear icon and typing)
  const [filterStatusInput, setFilterStatusInput] = useState<string>("");
  // Separate input value for Autocomplete
  // Status for modal form: always string
  const [status, setStatus] = useState<string>("Active");
  const [isSaving, setIsSaving] = useState(false);
  const [editItem, setEditItem] = useState<Technology | null>(null);
  const [error, setError] = useState("");
  const [banner, setBanner] = useState<{ type: "success" | "error"; message: string } | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [nameError, setNameError] = useState("");
  const [statusError, setStatusError] = useState("");
  // Accessibility: modal focus trap and auto-focus
  const modalRef = useRef<HTMLDivElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
      // Focus trap and auto-focus for modal
      useEffect(() => {
        if (!showModal) return;
        // Focus the name input when modal opens
        requestAnimationFrame(() => {
          nameInputRef.current?.focus();
        });
      }, [showModal]);

      const handleModalKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key !== 'Tab' || !modalRef.current) return;

        const focusable = Array.from(
          modalRef.current.querySelectorAll<HTMLElement>(
            'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
          )
        ).filter((el) => el.offsetParent !== null);

        if (focusable.length === 0) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const active = document.activeElement as HTMLElement | null;

        if (e.shiftKey) {
          if (active === first || !modalRef.current.contains(active)) {
            e.preventDefault();
            last.focus();
          }
          return;
        }

        if (active === last) {
          e.preventDefault();
          first.focus();
        }
      };
    const openAddModal = () => {
        resetForm();
        setShowModal(true);
    };

    const openEditModal = (item: Technology) => {
        resetForm();
        setEditItem(item);
        setName(item.technologyName);
        setStatus(item.status);
        setShowModal(true);
    };
    const validateForm = () => {
        let valid = true;

        const trimmed = name ? name.trim() : "";

        // NAME REQUIRED
        if (!trimmed) {
          setNameError("Technology name is required");
          valid = false;
        }
        // MIN LENGTH (now 3)
        else if (trimmed.length < 3) {
          setNameError("Minimum 3 characters required");
          valid = false;
        }
        // MAX LENGTH
        else if (trimmed.length > 30) {
          setNameError("Please limit to 30 characters");
          valid = false;
        }
        // ONLY SPECIAL CHARS/NUMBERS
        else if (!/[a-zA-Z]/.test(trimmed)) {
          setNameError("Technology name cannot be only numbers or special characters");
          valid = false;
        }
        else {
          // DUPLICATE CHECK (normalize for spaces, slashes, case, etc.)
          const normalizedInput = normalizeTechName(trimmed);
          const duplicate = technologies.some(t => {
            const existing = normalizeTechName(t.technologyName ?? "");
            return (
              existing === normalizedInput &&
              (!editItem || t.techGroupId !== editItem.techGroupId)
            );
          });
          if (duplicate) {
            setNameError("Technology already exists");
            valid = false;
          } else {
            setNameError("");
          }
        }

        // STATUS
        if (!status) {
            setStatusError("Status required");
            valid = false;
        } else {
            setStatusError("");
        }

        return valid;
    };
    const handleSave = async () => {
    if (isSaving) return;
 
    const trimmedName = name.trim();

    if (!validateForm()) return;
    setIsSaving(true);

    setError("");

    try {
      let response: Technology;

      if (editItem) {
        response = await apiPut(`/technology-details/${editItem.techGroupId}`, {
          name: trimmedName,
          status,
        });

        setTechnologies(prev =>
          prev.map(t =>
            t.techGroupId === response.techGroupId ? response : t
          )
        );

        // Do NOT reset page after edit
        setBanner({
          type: "success",
          message: "Technology has been updated successfully"
        });

        setSnackbarOpen(true);

      } else {

        response = await apiPost('/technology-details', {
          name: trimmedName,
          status,
        });

        setTechnologies(prev => [response, ...prev]);

        setSearchTerm("");
        setFilterStatus(null);
        setFilterStatusInput("");
        setCurrentPage(1);

        setBanner({
          type: "success",
          message: "Technology has been created successfully"
        });

        setSnackbarOpen(true);
      }

      setTimeout(() => setBanner(null), 3000);

      resetForm();

      setShowModal(false);

    } catch (err) {

      setBanner({
        type: "error",
        message: "An error occurred. Please try again."
      });

      setSnackbarOpen(true);

      console.error('Save failed', err);
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    fetchTechnologies();
  }, []);

  const fetchTechnologies = async () => {
    try {
      const data = await apiGet<Technology[]>("/technology-details");
      setTechnologies(data ?? []);
    } catch (error) {
      console.error("Failed to load technologies", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredData = useMemo(() => {
    let data = technologies;

    // Only filter by status if a value is selected
    if (filterStatus) {
      data = data.filter((item: any) => item.status === filterStatus);
    }

    if (searchTerm) {
      const lower = searchTerm.toLowerCase();

      data = data.filter((item: any) => {
        return Object.entries(item).some(([key, val]) => {

          if (!val) return false;

          if (key.toLowerCase().includes("date")) {

            let formatted = "";

            if (
              typeof val === "string" ||
              val instanceof Date ||
              val === null ||
              val === undefined
            ) {
              formatted = dateFormat(val)?.toLowerCase() || "";
            }

            return (
              formatted.includes(lower) ||
              String(val).toLowerCase().includes(lower)
            );
          }

          return String(val).toLowerCase().includes(lower);
        });
      });
    }

    return data;

  }, [technologies, searchTerm, filterStatus]);

    const sortedData = useMemo(() => {
        let data = [...filteredData];

        // ⭐ Default latest created date first
        if (!sortColumn) {
            return data.sort(
                (a, b) =>
                    new Date(b.createdDate ?? 0).getTime() -
                    new Date(a.createdDate ?? 0).getTime()
            );
        }

        data.sort((a: any, b: any) => {
          let valA = a?.[sortColumn];
          let valB = b?.[sortColumn];

          // null handling
          if (valA == null) return 1;
          if (valB == null) return -1;

          // ⭐ Robust DATE sort (supports ISO and DD-MMM-YYYY)
          if (String(sortColumn).toLowerCase().includes("date")) {
            const parseDate = (v: any) => {
              if (!v) return -Infinity;
              let d = dayjs(v); // Try ISO/standard
              if (!d.isValid() && typeof v === "string") {
                d = dayjs(v, "DD-MMM-YYYY");
              }
              return d.isValid() ? d.valueOf() : -Infinity;
            };
            const t1 = parseDate(valA);
            const t2 = parseDate(valB);
            return sortDirection === "asc" ? t1 - t2 : t2 - t1;
          }

          // ⭐ NUMBER sort (if future id added)
          if (!isNaN(valA) && !isNaN(valB)) {
            return sortDirection === "asc"
              ? Number(valA) - Number(valB)
              : Number(valB) - Number(valA);
          }

          // ⭐ STATUS sort (Active first)
          if (sortColumn === "status") {
            const orderMap: any = { Active: 1, Inactive: 2 };
            return sortDirection === "asc"
              ? orderMap[valA] - orderMap[valB]
              : orderMap[valB] - orderMap[valA];
          }

          // ⭐ STRING sort
          return sortDirection === "asc"
            ? String(valA).localeCompare(String(valB))
            : String(valB).localeCompare(String(valA));
        });

        return data;
    }, [filteredData, sortColumn, sortDirection]);

    const totalPages = Math.ceil(sortedData.length / PAGE_SIZE);
    const paginatedData = sortedData.slice(
        (currentPage - 1) * PAGE_SIZE,
        currentPage * PAGE_SIZE
    );

    const handleSort = (column: keyof Technology) => {
        setCurrentPage(1);   // ⭐ important
        // Disable sorting if there are no records
        if (sortedData.length === 0) return;
        if (sortColumn === column) {
          setSortDirection(sortDirection === "asc" ? "desc" : "asc");
        } else {
          setSortColumn(column);
          setSortDirection("asc");
        }
      };
    const resetForm = () => {
      setName("");
      setStatus("");
      setEditItem(null);
      setError("");
      setNameError("");    
      setStatusError("");
    };
  const renderSortIcon = (column: keyof Technology) => {
    // Disable sort icons if there are no records
    if (sortedData.length === 0) {
      return <span className="sort-icons disabled"><ArrowUpwardIcon className="sort-icon" /><ArrowDownwardIcon className="sort-icon" /></span>;
    }
    const isActive = sortColumn === column;
    return (
      <span className="sort-icons">
        <ArrowUpwardIcon className={`sort-icon ${isActive && sortDirection === "asc" ? "active" : ""}`} />
        <ArrowDownwardIcon className={`sort-icon ${isActive && sortDirection === "desc" ? "active" : ""}`} />
      </span>
    );
  };

  if (loading) return <div className="categories-loading">Loading...</div>;


  return (
      <div className="technologies-container technologies-layout" >
      <div className="technologies-top">
        <h2 className="categories-heading">Technologies</h2>
        <div className="technologies-controls">
          <button className="btn-primary" onClick={openAddModal}>
            <LocalIcon src="/icons/AddIcon.svg" className="btn-icon" /> Add Technology
          </button>

          {/* Status Filter Dropdown */}
          {/* Enhanced Status Filter Dropdown */}
          <Autocomplete
            className="filter-autocomplete status-filter"
            options={Array.from(new Set(technologies.map((item) => item.status)))}
            value={filterStatus}
            inputValue={filterStatusInput}
            forcePopupIcon
            clearIcon={null}
            onInputChange={(_event, newInputValue) => {
              setFilterStatusInput(newInputValue);
            }}
            onChange={(_event, newValue) => {
              setFilterStatus(newValue);
              setFilterStatusInput(newValue || "");
              setCurrentPage(1);
            }}
            filterOptions={(options, { inputValue }) =>
              options.filter(option =>
                option.toLowerCase().includes(inputValue.toLowerCase())
              )
            }
            size="small"
            clearOnEscape
            sx={{
              '& .MuiAutocomplete-clearIndicator': {
                color: '#6b7280',
                padding: 0,
                marginRight: '2px',
                '&:hover': {
                  background: 'rgba(0,0,0,0.04)',
                  color: '#6b7280',
                },
                '& svg': {
                  fontSize: '18px',
                  fontWeight: 400,
                },
              },
              '@media (prefers-color-scheme: dark)': {
                '& .MuiAutocomplete-popupIndicator': { color: '#fff' },
                '& .MuiAutocomplete-clearIndicator': { color: '#fff' },
              },
            }}
            renderInput={(params) => {
              const { InputProps, ...rest } = params;
              return (
                <TextField
                  {...rest}
                 InputProps={{
  ...InputProps,
  endAdornment: (
    <>
      {(filterStatusInput || filterStatus) && (
  <IconButton
    size="small"
    aria-label="clear status filter"
    onClick={() => {
      setFilterStatus(null);
      setFilterStatusInput("");
      setCurrentPage(1);
    }}
    edge="end"
    sx={{
      padding: '2px',
      marginLeft: '36px',
      '@media (prefers-color-scheme: dark)': {
        color: '#fff',
        '& .MuiSvgIcon-root': {
          color: '#fff',
        },
      },
    }}
  >
    <ClearIcon fontSize="small" />
  </IconButton>
)}

      {InputProps.endAdornment}
    </>
  )
}}
                  placeholder="Status"
                  variant="outlined"
                  className="filter-input"
                />
              );
            }}
          />

          <TextField
            size="small"
            placeholder="Search"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            className="input-search"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon className="search-icon" />
                </InputAdornment>
              ),
              endAdornment: (
                searchTerm ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      aria-label="clear search"
                      onClick={() => { setSearchTerm(''); setCurrentPage(1); }}
                      edge="end"
                      sx={{
                        '@media (prefers-color-scheme: dark)': {
                          color: '#fff',
                          '& .MuiSvgIcon-root': {
                            color: '#fff',
                          },
                        },
                      }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null
              ),
            }}
          />
        </div>
      </div>

      <div className="table-card">
          <table className="technologies-table">
            <thead>
              <tr>
                <th onClick={() => handleSort("technologyName")}> <div className="th-inner">Name {renderSortIcon("technologyName")}</div> </th>
                <th onClick={() => handleSort("createdBy")}> <div className="th-inner">Created By {renderSortIcon("createdBy")}</div> </th>
                <th onClick={() => handleSort("createdDate")}> <div className="th-inner">Created Date {renderSortIcon("createdDate")}</div> </th>
                <th onClick={() => handleSort("modifiedBy")}> <div className="th-inner">Updated By {renderSortIcon("modifiedBy")}</div> </th>
                <th onClick={() => handleSort("modifiedDate")}> <div className="th-inner">Updated Date {renderSortIcon("modifiedDate")}</div> </th>
                <th onClick={() => handleSort("status")}> <div className="th-inner">Status {renderSortIcon("status")}</div> </th>
                <th className="action-header">Action</th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.length === 0 ? (
                <tr>
                   <td colSpan={7} className="empty-state">
                        <div className="empty-content">
                           <p className="th-inner">No Technologies found.<br/>
                            Try a different search or add a new Technology.
                           </p>
                        </div>
                   </td>
                </tr>
              ) : (
                paginatedData.map((t) => (
                  <TechnologyTableRow key={t.techGroupId} t={t} openEditModal={openEditModal} />
                ))
              )}
            </tbody>
          </table>
          </div>
          {/* ⭐ Pagination just below table */}
          {/* Show pagination only if there are records */}
          {sortedData.length > 0 && (
            <div className="pagination-container technologies-pagination">
              <div className="pagination-info">
                {sortedData.length === 0
                  ? 0
                  : (currentPage - 1) * PAGE_SIZE + 1}{" "}
                to{" "}
                {Math.min(currentPage * PAGE_SIZE, sortedData.length)}{" "}
                of {sortedData.length} | Page {currentPage} of {totalPages || 1}
              </div>
              <button
                className="page-btn first-btn"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(1)}
              >
                First
              </button>
              <button
                className="page-btn prev-btn"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </button>
              <div className="page-numbers">
                {(() => {
                  const blockSize = 5;
                  const currentBlock = Math.floor((currentPage - 1) / blockSize);
                  const startPage = currentBlock * blockSize + 1;
                  const endPage = Math.min(startPage + blockSize - 1, totalPages);
                  const pageButtons = [];
                  for (let p = startPage; p <= endPage; p++) {
                    pageButtons.push(
                      <button
                        key={p}
                        className={`page-number ${p === currentPage ? 'active' : ''}`}
                        onClick={() => setCurrentPage(p)}
                      >
                        {p}
                      </button>
                    );
                  }
                  return pageButtons;
                })()}
              </div>
              <button
                className="page-btn next-btn"
                disabled={currentPage === totalPages || totalPages === 0}
                onClick={() => setCurrentPage((p) => Math.min(totalPages || 1, p + 1))}
              >
                Next
              </button>
              <button
                className="page-btn last-btn"
                disabled={currentPage === totalPages || totalPages === 0}
                onClick={() => setCurrentPage(totalPages)}
              >
                Last
              </button>
            </div>
          )}
      {showModal && (
        <div className="modal-overlay">
          <div
            className="modal"
            ref={modalRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="technology-modal-title"
            onKeyDown={handleModalKeyDown}
          >
            <h3 id="technology-modal-title">{editItem ? "Edit Technology" : "Add Technology"}</h3>
            <label>
              Technology Name <span className="required">*</span>
            </label>
            <div style={{ position: 'relative' }}>
              <input
                ref={nameInputRef}
                value={name}
                maxLength={30}
                onChange={(e) => {
                  const rawValue = e.target.value;
                  const sanitizedValue = rawValue.replace(/^\s+/, "").replace(/\s{2,}/g, " ");
                  setName(sanitizedValue);
                  if (nameError) setNameError("");
                }}
                placeholder="Enter technology name"
                style={{ paddingRight: name ? 32 : undefined }}
              />
              {name && (
                <button
                  type="button"
                  aria-label="clear technology name"
                  onClick={() => setName("")}
                  style={{
                    position: 'absolute',
                    right: 4,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    margin: 0,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    zIndex: 2,
                    ...(window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
                      ? { color: '#f1f5f9' }
                      : {}),
                  }}
                >
                  <ClearIcon fontSize="small" />
                </button>
              )}
            </div>
            <div
              style={{
                fontSize: '12px',
                color: (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) ? '#fff' : '#000',
                marginTop: 2,
                marginBottom: 2
              }}
            >
              {name.length}/30 characters
            </div>

            {nameError && (
              <div className="field-error">
                {nameError}
              </div>
            )}

            <label>
              Status <span className="required">*</span>
            </label>
            <select
              value={status ?? ''}
              onChange={(e) => {
                setStatus(e.target.value);
                if (statusError) setStatusError("");
              }}
              style={{
                color: undefined,
                background: undefined,
                ...(window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
                  ? { color: '#fff', background: '#232c3a' }
                  : {}),
              }}
            >
              <option value="">Select Status</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>

            {statusError && (
              <div className="field-error">
                {statusError}
              </div>
            )}
            {error && <div className="field-error">{error}</div>}
            <div className="modal-actions">
              <button className="btn-primary" onClick={handleSave} disabled={isSaving}>
                {isSaving ? "Saving..." : (editItem ? "Update" : "Create")}
              </button>
              <button
                onClick={() => {
                  resetForm();
                  setShowModal(false);
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      <Snackbar
        open={snackbarOpen && !!banner}
        autoHideDuration={4000}
        onClose={() => { setSnackbarOpen(false); setBanner(null); }}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{ zIndex: 1400 }}
      >
        <Alert
          onClose={() => { setSnackbarOpen(false); setBanner(null); }}
          severity={banner?.type || 'success'}
          icon={banner?.type === 'success' ? <CheckCircleIcon fontSize="inherit" sx={{ color: '#2e7d32' }} /> : undefined}
          sx={{
            width: '100%',
            fontWeight: 500,
            fontSize: 15,
            alignItems: 'center',
            backgroundColor: banner?.type === 'success' ? '#e6f4ea' : '#fdecea',
            color: banner?.type === 'success' ? '#2e7d32' : '#d32f2f',
            boxShadow: 3,
            '@media (prefers-color-scheme: dark)': {
              backgroundColor: banner?.type === 'success' ? '#1b2e1b' : '#2d1616',
              color: banner?.type === 'success' ? '#7ee787' : '#ffb4ab',
            },
          }}
        >
          <span className="app-snackbar-message">{banner?.message || ''}</span>
        </Alert>
      </Snackbar>

        
    </div>
  );
};

export default TechnologiesDetails;
